import { type MouseEvent, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Icon, type MenuProps } from 'ui';

export type JoinButtonStatus = 'accept-reject' | 'accepted' | 'join' | 'joined' | 'pending' | 'rejected' | 'request';

type P = {
  children?: ReactNode;
  disabled?: boolean;
  dropdown?: MenuProps;
  loading?: boolean;
  onClick?: ((event: MouseEvent<HTMLElement>) => Promise<void>) | ((event: MouseEvent<HTMLElement>) => void);
  onRejectClick?: (() => Promise<void>) | (() => void);
  size?: 'middle' | 'small';
  status?: JoinButtonStatus;
};

type ReducerAction = { status: JoinButtonStatus };

type ReducerState = {
  className?: string;
  icon?: ReactNode;
  title: string;
  type: 'primary' | 'tertiary';
};

const reduceData = ({ status }: ReducerAction): ReducerState => {
  switch (status) {
    case 'accept-reject':
      return {
        className: 'join-status-button--accept',
        icon: <Icon icon="check-circle-line" />,
        title: 'Accept',
        type: 'primary',
      };
    case 'accepted':
      return {
        className: 'join-status-button--accepted',
        icon: <Icon icon="check-circle-line" />,
        title: "Yes, I'm going",
        type: 'tertiary',
      };
    case 'join':
      return { className: 'join-status-button--join', title: 'Join', type: 'primary' };
    case 'joined':
      return {
        className: 'join-status-button--joined',
        icon: <Icon icon="check-circle-line" />,
        title: 'Joined',
        type: 'tertiary',
      };
    case 'pending':
      return {
        className: 'join-status-button--pending',
        icon: <Icon icon="check-circle-line" />,
        title: 'Request sent',
        type: 'tertiary',
      };
    case 'rejected':
      return {
        className: 'join-status-button--rejected',
        icon: <Icon icon="close-circle-line" />,
        title: "No, I can't make it",
        type: 'tertiary',
      };
    case 'request':
      return { icon: <Icon icon="add-circle-line" />, title: 'Request to Join', type: 'primary' };
  }
};

const JoinStatusButton = ({
  children,
  disabled,
  dropdown,
  loading,
  onClick,
  onRejectClick,
  size = 'middle',
  status = 'join',
}: P) => {
  const { t } = useTranslation();
  const { title, ...buttonStateProperties } = useMemo(() => reduceData({ status }), [status]);

  const text = useMemo((): ReactNode => {
    if (children) {
      return typeof children === 'string' ? t(children) : children;
    }

    return t(title);
  }, [title, children, t]);

  const renderRejectButton = () => {
    if (status !== 'accept-reject') {
      return null;
    }

    return (
      <Button
        className="join-status-button--reject"
        disabled={!onRejectClick || disabled}
        icon={<Icon icon="close-circle-line" />}
        loading={loading}
        onClick={onRejectClick}
        size={size}
      />
    );
  };

  return dropdown ? (
    <>
      <Dropdown disabled={disabled} menu={dropdown} trigger={['click']}>
        <Button loading={loading} size={size} {...buttonStateProperties}>
          {text}
          <Icon icon="down-line" />
        </Button>
      </Dropdown>
      {renderRejectButton()}
    </>
  ) : (
    <>
      <Button
        loading={loading}
        size={size}
        {...buttonStateProperties}
        disabled={!onClick || disabled}
        onClick={onClick}
      >
        {text}
      </Button>
      {renderRejectButton()}
    </>
  );
};

export default JoinStatusButton;
